import React, { useEffect, useState } from 'react';
import { useNavigate, useRoutes, useParams } from 'react-router-dom';
import { useUTMSource } from '../components/Context/UTMSourceProvider';
import { MainLayout } from '../layout/MainLayout';
import { Authlayout } from '../layout/Authlayout';
import Home from '../pages/HomeMain';
import PackageListing from '../pages/PackageListing';
import PackageDetails from '../pages/PackageDetails';
import Conversational from '../pages/Conversational';
import SelectedVacationSpot from '../components/conversational/SelectedVacationSpot/SelectedVacationSpot';
import ProductReview from '../pages/ProductReview/ProductReview';
import TripRequestConfirmation from '../pages/TripRequestConfirmation';
import PhotoGallery from '../pages/PhotoGallery/PhotoGallery';
import NotFound404 from '../pages/NotFound404';
import PrivacyPolicy from '../pages/PrivacyPolicy';

import DynamicDestinationPage from '../pages/DynamicDestinationPage';
// import DynamicDestinationPhotoGallery from '../pages/DynamicDestinationPhotoGallery';

const DynamicDestinationGuard = ({ element }) => {
  const { slug } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [isNotFound, setIsNotFound] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const validateRoute = async () => {
      try {
        const URL = `${process.env.REACT_APP_API_BASE_URL}/cms-page/${slug}`;
        console.log('Guard calling URL:', URL);

        const response = await fetch(URL);

        if (response.status === 404) {
          if (isMounted) setIsNotFound(true);
        }
      } catch (error) {
        console.error('Guard Error:', error);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    validateRoute();

    return () => {
      isMounted = false;
    };
  }, [slug]);

  if (isLoading) return null;
  if (isNotFound) return <NotFound404 />;

  return element;
};

const authRoutes = [
  { path: '/', element: <Home /> },
  { path: '/product-review', element: <ProductReview /> },
  { path: '/explore/product-details/:package-id', element: <PackageDetails /> },
  { path: '/explore/:country/:countrysite/:state/:package-id', element: <PackageDetails /> },
  { path: '/explore/*', element: <PackageListing /> },
  { path: '/conversational', element: <Conversational /> },
  // { path: '/vacation-spot', element: <SelectedVacationSpot /> },
  {
    path: '/explore/:country/:countrysite/:state/:package-id/vacation-spot',
    element: <SelectedVacationSpot />
  },
  { path: '/photo-gallery', element: <PhotoGallery /> },
  {
    path: '/explore/:country/:countrysite/:state/:package-id/trip-confirmation',
    element: <TripRequestConfirmation />
  },
  {
    path: '/destination/:slug',
    element: <DynamicDestinationGuard element={<DynamicDestinationPage />} />
  },
  {
    path: '/destination/:slug/packages',
    element: <DynamicDestinationGuard element={<DynamicDestinationPage section="packages" />} />
  },
  // {
  //   path: '/destination/:slug/photo-gallery',
  //   element: <DynamicDestinationGuard element={<DynamicDestinationPhotoGallery />} />
  // },
  {
    path: '/destination/:slug/:package-id',
    element: <DynamicDestinationGuard element={<PackageDetails />} />
  },
  {
    path: '/destination/:slug/:package-id/vacation-spot',
    element: <DynamicDestinationGuard element={<SelectedVacationSpot />} />
  },
  {
    path: '/destination/:slug/:package-id/trip-confirmation',
    element: <DynamicDestinationGuard element={<TripRequestConfirmation />} />
  }
];

const useUTMPersistentNavigate = () => {
  const navigate = useNavigate();
  const utmSource = useUTMSource();
  return (to, options) => {
    if (typeof to === 'string' && utmSource) {
      const url = new URL(to, window.location.origin);
      const params = new URLSearchParams(url.search);
      if (!params.get('utm_source')) {
        params.set('utm_source', utmSource);
        to = `${url.pathname}?${params.toString()}`;
      }
    }
    navigate(to, options);
  };
};

const RouteWithUTMSource = ({ element, utmSource, navigate }) => {
  useEffect(() => {
    if (utmSource) {
      const currentPathname = window.location.pathname;
      const currentSearchParams = new URLSearchParams(window.location.search);
      if (!currentSearchParams.get('utm_source')) {
        currentSearchParams.set('utm_source', utmSource);
        const updatedUrl = `${currentPathname}?${currentSearchParams.toString()}`;
        if (window.location.href !== window.location.origin + updatedUrl) {
          navigate(updatedUrl, { replace: true });
        }
      }
    }
  }, [utmSource, navigate]);
  return element;
};

const appendUTMSourceToRoutes = (routes, utmSource, navigate) =>
  routes.map((route) => {
    if (route.path === '/vacation-spot') {
      return { ...route };
    }
    return {
      ...route,
      element: (
        <RouteWithUTMSource element={route.element} utmSource={utmSource} navigate={navigate} />
      ),
      children: route.children
        ? appendUTMSourceToRoutes(route.children, utmSource, navigate)
        : undefined
    };
  });

const Routes = () => {
  const utmSource = useUTMSource();
  const navigate = useUTMPersistentNavigate();

  const enhancedRoutes = appendUTMSourceToRoutes(
    [
      {
        path: '/',
        element: <Authlayout />,
        children: authRoutes
      },
      {
        path: '/auth',
        element: <MainLayout />,
        children: []
      },
      {
        path: '/privacy-policy',
        element: <PrivacyPolicy />
      },

      { path: '*', element: <NotFound404 /> }
    ],
    utmSource,
    navigate
  );

  return useRoutes(enhancedRoutes);
};

export default Routes;
